import Components from "slotworks/components/components";
import {Services} from "slotworks/services/services";
import {arrayOfValues} from "slotworks/utils/collection-utils";
import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import Sequence from "slotworks/utils/contracts/sequence";
import Point from "slotworks/utils/geom/point";
import SGWildSpineAnimation from "./sg-wild-spine-animation";

type WinPatternInfo = {reel: number, type: string;};

export const WILD_COMBINATIONS = {RA: "ra", MAAT: "maat", KHNUM: "khnum", RA_MAAT: "ra_maat", MAAT_KHNUM: "maat_khnum", RA_MAAT_KHNUM: "ra_maat_khnum"};

class SGWildsComponent {

    private spineAnimations: SGWildSpineAnimation[] = [];
    private contracts: Contract<any>[] = [];

    private winPatterns: {[key: string]: WinPatternInfo[];} = {
        "100": [{reel: 1, type: WILD_COMBINATIONS.RA}],
        "010": [{reel: 2, type: WILD_COMBINATIONS.MAAT}],
        "001": [{reel: 3, type: WILD_COMBINATIONS.KHNUM}],
        "110": [{reel: 1, type: WILD_COMBINATIONS.RA_MAAT}],
        "101": [{reel: 1, type: WILD_COMBINATIONS.RA}, {reel: 3, type: WILD_COMBINATIONS.KHNUM}],
        "011": [{reel: 2, type: WILD_COMBINATIONS.MAAT_KHNUM}],
        "111": [{reel: 1, type: WILD_COMBINATIONS.RA_MAAT_KHNUM}],
    };

    public init() {
        for (let i = 0; i < 3; i++) {
            this.spineAnimations.push(new SGWildSpineAnimation(i));
        }
    }

    public isExpandedReel(reelIndex: number): boolean {
        return Components.matrix.getSymbol(reelIndex, 0).symbolId.indexOf("expanded_") > -1;
    }

    public clearExpandedReels(): void {
        this.resetSpines();
    }

    public playExpendAnimation(position: Point): Contract<void> {
        const reel: number = position.x;
        const row: number = position.y;

        const anim: SGWildSpineAnimation = this.getSpineAnim(reel);
        const animationContract: Contract<void> = new Sequence([
            () => anim.playExpandAnimation(reel, row),
            () => Contract.wrap(() => this.showExpandedSymbol(position)),
        ]);

        this.contracts.push(animationContract);
        return animationContract;
    }

    public playGloomSound(): Contract<void> {
        return new Contract(resolve => {
            Services.sound.customEvent("wild_gloom");
            resolve(null);
        });
    }

    private showExpandedSymbol(sourcePosition: Point): void {
        for (let y = 0; y < 3; y++) {
            const symbol = Components.matrix.getSymbol(sourcePosition.x, y);
            symbol.setSymbol("expanded_" + (sourcePosition.x + 1));
            symbol.setVisible(true);
            symbol.static();
        }
    }

    public showStaticWild(reel: number): void {
        this.spineAnimations[reel - 1].showStaticFrame(reel);
    }

    public playCombineAnimation(combination: string[], combinedReelIndexes: number[]): Contract<void> {
        this.resetSpines();
        const anim: SGWildSpineAnimation = this.getSpineAnim(combinedReelIndexes[0]);
        const animationContract: Contract<void> = anim.playCombineAnimation(combination);
        this.contracts.push(animationContract);
        return animationContract;
    }

    public playBonusItro(flags: boolean[]): Contract<void> {
        this.resetSpines();
        let animationContracs = [];
        let patternId = flags.map(flag => Number(flag)).join("");
        for (let i = 0; i < this.winPatterns[patternId].length; i++) {
            let patternInfo = this.winPatterns[patternId][i];
            const anim: SGWildSpineAnimation = this.getSpineAnim(patternInfo.reel);
            const animationContract: Contract<void> = new Parallel([
                () => anim.playWinAnimation(patternInfo.type)
            ]);
            animationContracs.push(() => animationContract);
            this.contracts.push(animationContract);
        }

        return new Parallel(animationContracs);
    }

    public playWinAnimation(flags: boolean[]): Contract<void> {
        this.resetSpines();
        let animationContracs = [];
        let patternId = flags.map(flag => Number(flag)).join("");
        for (let i = 0; i < this.winPatterns[patternId].length; i++) {
            let patternInfo = this.winPatterns[patternId][i];
            const anim: SGWildSpineAnimation = this.getSpineAnim(patternInfo.reel);
            const animationContract: Contract<void> = new Parallel([
                () => anim.playWinAnimation(patternInfo.type),
                () => this.playGloomSound()
            ]);
            animationContracs.push(() => animationContract);
            this.contracts.push(animationContract);
        }

        return new Parallel(animationContracs);
    }

    public playHighlightAnimation(flags: boolean[]): Contract<void> {
        this.resetSpines();
        let animationContracs = [];
        let patternId = flags.map(flag => Number(flag)).join("");
        for (let i = 0; i < this.winPatterns[patternId].length; i++) {
            let patternInfo = this.winPatterns[patternId][i];
            const anim: SGWildSpineAnimation = this.getSpineAnim(patternInfo.reel);
            const animationContract: Contract<void> = new Parallel([
                () => anim.playHighlightAnimation(patternInfo.type)
            ]);
            animationContracs.push(() => animationContract);
            this.contracts.push(animationContract);
        }

        return new Parallel(animationContracs);
    }

    private getSpineAnim(reelIndex: number): SGWildSpineAnimation {
        return this.spineAnimations[reelIndex - 1];
    }

    public skip(): void {
        this.cancelContracts();
    }

    private cancelContracts(): void {
        this.contracts.forEach(contrac => {
            contrac.cancel();
        });
    }

    private resetSpines(): void {
        this.spineAnimations.forEach(spine => {
            spine.reset();
        });
    }



}

export default new SGWildsComponent();
